import { useEffect } from 'react';
import { Flexbox, Text } from '@morressier/ts';
import { Button, LinkButton } from 'components/ProfileUIComponents';
import styled from 'styled-components';
import { faXmark, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRouter } from 'next/router';
import { getQuery } from 'utils/query';
import { VIEWS } from '../EmailAffiliation';
import { ModalImage } from '../../../styles';
import { logEvent } from 'utils/eventTracking';

const Header = styled.div`
  border-radius: 8px;
  padding: 1rem;
  background-color: var(--color-mono-99);
  text-align: right;
`;

const Content = styled(Flexbox)`
  height: 100%;
  overflow-y: auto;
  padding: 32px;
  background-color: var(--color-mono-99);
  text-align: center;
`;

const Container = styled(Flexbox)`
  height: 100%;
`;

const CloseButton = styled(LinkButton)`
  margin-left: auto;
`;

const Description = styled(Text)`
  width: 34rem;
  margin-bottom: 2rem;
`;

interface IEmailValidationViewProps {
  email: string;
  type: string;
  isEmailDomainMatchesOrgDomain: boolean;
  handleNavigaiton: (nextView: VIEWS) => void;
  onDismiss: () => void;
  resetForm: () => void;
  showCTAButton?: boolean; // for call to action buttons
}

const EmailValidationView = (props: IEmailValidationViewProps) => {
  const router = useRouter();
  const queryParams = getQuery(router);
  const isAccountActivation = queryParams.flow === 'account-activation';
  const {
    handleNavigaiton,
    onDismiss,
    resetForm,
    email,
    type,
    isEmailDomainMatchesOrgDomain,
    showCTAButton = true
  } = props;

  useEffect(() => {
    if (isAccountActivation && queryParams.redirectRoute) {
      window.location.replace(queryParams.redirectRoute.toString());
    }

    isEmailDomainMatchesOrgDomain &&
      logEvent('PROFILE_VALIDATE_AFFILIATION_ADD_VALIDATION_EMAIL_SENT');
  }, [isAccountActivation, isEmailDomainMatchesOrgDomain]);

  const onClickButton = () => {
    logEvent('PROFILE_ADD_AFFILIATION_ADD_ANOTHER_AFFILIATION_CLICKED');
    resetForm();
    handleNavigaiton(VIEWS.EmailView);
  };

  const onClose = () => {
    if (isAccountActivation) {
      router.replace(`/profile/${router.query.id}`);
    }
    onDismiss();
  };

  return (
    <Container flexDirection="column">
      <Header>
        <CloseButton onClick={onClose}>
          close
          <FontAwesomeIcon icon={faXmark} />
        </CloseButton>
      </Header>
      <Content
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
      >
        <ModalImage src="/home-client/shared/svg-check-inbox.svg" />
        {isAccountActivation ? (
          <Text size="h3_new" fontWeight="bold">
            Account created
          </Text>
        ) : (
          isEmailDomainMatchesOrgDomain && (
            <Text size="h3_new" fontWeight="bold">
              Check your inbox.
            </Text>
          )
        )}
        {isAccountActivation ? (
          <Description>
            You have added your affiliation and completed setup of your account.
          </Description>
        ) : (
          <Description>
            {isEmailDomainMatchesOrgDomain
              ? `We've sent you a link to ${email} to complete the verification of
              this affiliation.`
              : 'Affiliation has been created.'}
          </Description>
        )}
        {showCTAButton && (
          <Button
            color="secondary"
            onClick={onClickButton}
            iconBefore={<FontAwesomeIcon icon={faPlus} />}
          >
            Add another affiliation
          </Button>
        )}
      </Content>
    </Container>
  );
};
export default EmailValidationView;
